// ═══════════════════════════════════════════════════════════════════════════
// EnginePageShell — root wrapper for every page
// packages/@forgetrack/engine/src/EnginePageShell.tsx
// Version: 2026.4.24 (lifted from apps/web; brand + footer made configurable)
// ═══════════════════════════════════════════════════════════════════════════

import React, {
  type ReactNode,
  useState,
  useRef,
  useEffect,
  useCallback,
  createContext,
  useContext,
} from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F, ENGINE_KEYFRAMES as K } from './tokens';
import { useClock, useLiveTick } from './hooks';

// ─── UI Density / Font Scale ─────────────────────────────────────────────────

type UIDensity = 'compact' | 'comfortable' | 'spacious' | 'large';

const DENSITY_STORAGE_KEY = 'ft_ui_density';

const DENSITY_SCALE: Record<UIDensity, number> = {
  compact: 0.85,
  comfortable: 1.0,
  spacious: 1.15,
  large: 1.30,
};

export const FS = {
  chrome: 11,
  label: 11,
  small: 12,
  body: 13,
  heading: 14,
};

interface DensityContextValue {
  density: UIDensity;
  scale: number;
  setDensity: (d: UIDensity) => void;
  fs: (base: number) => number;
}

const DensityContext = createContext<DensityContextValue>({
  density: 'comfortable',
  scale: 1.0,
  setDensity: () => {},
  fs: (base) => base,
});

export function useDensity() {
  return useContext(DensityContext);
}

function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<UIDensity>(() => {
    if (typeof window === 'undefined') return 'comfortable';
    return (localStorage.getItem(DENSITY_STORAGE_KEY) as UIDensity) ?? 'comfortable';
  });

  const setDensity = useCallback((d: UIDensity) => {
    localStorage.setItem(DENSITY_STORAGE_KEY, d);
    setDensityState(d);
  }, []);

  const scale = DENSITY_SCALE[density];
  const fs = useCallback((base: number) => Math.round(base * scale), [scale]);

  return (
    <DensityContext.Provider value={{ density, scale, setDensity, fs }}>
      {children}
    </DensityContext.Provider>
  );
}

function DensityPicker() {
  const { density, setDensity } = useDensity();
  const options: { id: UIDensity; label: string; title: string }[] = [
    { id: 'compact', label: 'A', title: 'Compact — dense layout' },
    { id: 'comfortable', label: 'A', title: 'Comfortable — default' },
    { id: 'spacious', label: 'A', title: 'Spacious — larger text' },
    { id: 'large', label: 'A', title: 'Large — high visibility' },
  ];
  const labelSizes = [9, 11, 14, 17];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {options.map((opt, i) => {
        const isActive = density === opt.id;
        return (
          <button
            key={opt.id}
            title={opt.title}
            onClick={() => setDensity(opt.id)}
            style={{
              fontFamily: F.display,
              fontSize: labelSizes[i],
              fontWeight: isActive ? 700 : 400,
              color: isActive ? C.neon : C.dim,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0 3px',
              lineHeight: 1,
              opacity: isActive ? 1 : 0.6,
              transition: 'all 0.15s ease',
              textShadow: isActive ? `0 0 8px ${C.neonGlow ?? C.neon}` : 'none',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── EnginePageShell ────────────────────────────────────────────────────────

interface EnginePageShellProps {
  title: string;
  subtitle?: string;
  icon?: string;
  accentColor?: string;
  statusLabel?: string;
  statusColor?: string;
  children: ReactNode;
  actions?: ReactNode;
  /** Brand name shown in the top status bar. Defaults to "FORGETRACK". */
  brand?: string;
  /** Footer text in the bottom HUD. Defaults to "ForgeTrack · Edson Technologies". */
  footer?: string;
}

export default function EnginePageShell(props: EnginePageShellProps) {
  return (
    <DensityProvider>
      <EnginePageShellInner {...props} />
    </DensityProvider>
  );
}

function EnginePageShellInner({
  title,
  subtitle,
  icon = '⚡',
  accentColor = C.neon,
  statusLabel = 'LIVE',
  statusColor,
  children,
  actions,
  brand = 'FORGETRACK',
  footer = 'ForgeTrack · Edson Technologies',
}: EnginePageShellProps) {
  const clock = useClock();
  const tick = useLiveTick();
  const { fs, scale } = useDensity();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: F.display,
        color: C.text,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{K}</style>

      <div
        style={{
          position: 'fixed', inset: 0,
          backgroundImage: `
            linear-gradient(${accentColor}08 1px, transparent 1px),
            linear-gradient(90deg, ${accentColor}08 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          animation: 'engine-neural 25s linear infinite',
          pointerEvents: 'none', zIndex: 0,
        }}
      />
      <div
        style={{
          position: 'fixed', inset: 0,
          background: `linear-gradient(180deg, transparent 0%, ${accentColor}03 50%, transparent 100%)`,
          height: '200%',
          animation: 'engine-scan 8s linear infinite',
          pointerEvents: 'none', zIndex: 0,
        }}
      />
      <div
        style={{
          position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '120%', height: '400px',
          background: `radial-gradient(ellipse at 50% 0%, ${accentColor}12 0%, transparent 70%)`,
          pointerEvents: 'none', zIndex: 0,
        }}
      />

      <div
        style={{
          position: 'sticky', top: 0, zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 24px',
          background: 'rgba(5,9,16,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${C.faint}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: fs(FS.chrome), fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em' }}>
            {brand}
          </span>
          <span style={{ color: C.faint }}>│</span>
          <span style={{ fontSize: 18 }}>{icon}</span>
          <span style={{ fontSize: fs(FS.heading), fontWeight: 600, color: C.text }}>{title}</span>
          {subtitle && (
            <span style={{
              fontSize: fs(FS.chrome), fontFamily: F.mono,
              letterSpacing: '0.15em', color: C.muted,
              background: `${accentColor}15`,
              border: `1px solid ${accentColor}30`,
              padding: '2px 8px', borderRadius: 4,
            }}>
              {subtitle}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {actions}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PulseDot color={statusColor || accentColor} size={7} />
            <span style={{
              fontSize: fs(FS.chrome), fontFamily: F.mono,
              letterSpacing: '0.12em', color: statusColor || accentColor,
              opacity: tick ? 1 : 0.6, transition: 'opacity 0.3s',
            }}>
              {statusLabel}
            </span>
          </div>
          <span style={{ fontSize: fs(FS.chrome + 1), fontFamily: F.mono, color: C.dim }}>{clock}</span>
        </div>
      </div>

      <div
        style={{
          position: 'relative', zIndex: 1,
          maxWidth: 1400, margin: '0 auto',
          padding: '24px 24px 80px', zoom: scale,
        }}
      >
        {children}
      </div>

      <div
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 24px',
          background: 'rgba(5,9,16,0.95)',
          borderTop: `1px solid ${C.faint}`,
          fontSize: fs(FS.chrome), fontFamily: F.mono,
          letterSpacing: '0.1em', color: C.dim,
        }}
      >
        <span>{footer}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <DensityPicker />
        </div>
      </div>
    </div>
  );
}

// ─── GlassPanel ─────────────────────────────────────────────────────────────

interface GlassPanelProps {
  accent?: string;
  delay?: number;
  children: ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
  className?: string;
}

export function GlassPanel({ accent = C.neon, delay = 0, children, style, onClick }: GlassPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), delay * 1000);
    return () => clearTimeout(timeout);
  }, [delay]);

  return (
    <div
      ref={ref}
      onClick={onClick}
      style={{
        background: C.surface,
        border: `1px solid ${accent}20`,
        borderRadius: 12,
        padding: 20,
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : undefined,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute', inset: 0,
          background: `repeating-linear-gradient(180deg, transparent, transparent 2px, ${accent}03 2px, ${accent}03 4px)`,
          pointerEvents: 'none', borderRadius: 12,
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
}

// ─── PulseDot ───────────────────────────────────────────────────────────────

interface PulseDotProps { color?: string; size?: number; }

export function PulseDot({ color = C.neon, size = 8 }: PulseDotProps) {
  return (
    <span style={{
      position: 'relative', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center',
      width: size, height: size,
    }}>
      <span style={{
        position: 'absolute', width: '100%', height: '100%',
        borderRadius: '50%', background: color,
        animation: 'engine-ping 2s cubic-bezier(0,0,0.2,1) infinite',
        opacity: 0.4,
      }} />
      <span style={{
        width: '100%', height: '100%',
        borderRadius: '50%', background: color,
        boxShadow: `0 0 6px ${color}`,
      }} />
    </span>
  );
}

// ─── SectionLabel ───────────────────────────────────────────────────────────

interface SectionLabelProps {
  color?: string;
  children: ReactNode;
  style?: React.CSSProperties;
}

export function SectionLabel({ color = C.muted, children, style }: SectionLabelProps) {
  const { fs } = useDensity();
  return (
    <div style={{
      fontSize: fs(FS.label), fontFamily: F.mono, fontWeight: 600,
      letterSpacing: '0.15em', textTransform: 'uppercase',
      color, marginBottom: 12, ...style,
    }}>
      {children}
    </div>
  );
}

// ─── NeonValue ──────────────────────────────────────────────────────────────

interface NeonValueProps {
  color?: string;
  size?: number;
  glow?: boolean;
  children: ReactNode;
}

export function NeonValue({ color = C.neon, size = 32, glow = true, children }: NeonValueProps) {
  return (
    <span style={{
      fontSize: size, fontFamily: F.mono, fontWeight: 700, color,
      textShadow: glow ? `0 0 20px ${color}60` : undefined, lineHeight: 1,
    }}>
      {children}
    </span>
  );
}

// ─── EngineTooltip ──────────────────────────────────────────────────────────

interface EngineTooltipProps {
  content: ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom';
}

export function EngineTooltip({ content, children, position = 'top' }: EngineTooltipProps) {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const { fs } = useDensity();

  const handleEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        x: rect.left + rect.width / 2,
        y: position === 'top' ? rect.top : rect.bottom,
      });
    }
    setShow(true);
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={{ cursor: 'pointer', position: 'relative' }}
      >
        {children}
      </span>
      {show && (
        <div style={{
          position: 'fixed', left: coords.x,
          top: position === 'top' ? coords.y - 8 : coords.y + 8,
          transform: position === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
          zIndex: 1000, background: 'rgba(8,14,28,0.96)',
          border: `1px solid ${C.faint}`, borderRadius: 8,
          padding: '10px 14px', fontSize: fs(FS.body),
          fontFamily: F.display, color: C.textSoft, maxWidth: 320,
          animation: 'engine-tooltip-in 0.2s cubic-bezier(0.16,1,0.3,1)',
          backdropFilter: 'blur(8px)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          {content}
        </div>
      )}
    </>
  );
}

// ─── StatusBadge ────────────────────────────────────────────────────────────

interface StatusBadgeProps { label: string; color?: string; glow?: boolean; }

export function StatusBadge({ label, color = C.neon, glow = false }: StatusBadgeProps) {
  const { fs } = useDensity();
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: fs(FS.label), fontFamily: F.mono, fontWeight: 600,
      letterSpacing: '0.1em', textTransform: 'uppercase',
      color, background: `${color}12`,
      border: `1px solid ${color}30`,
      padding: '3px 8px', borderRadius: 4,
      boxShadow: glow ? `0 0 8px ${color}30` : undefined,
    }}>
      <PulseDot color={color} size={5} />
      {label}
    </span>
  );
}

// ─── MetricCard ─────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: unknown;
  subtitle?: string;
  color?: string;
  icon?: string;
  tooltip?: ReactNode;
}

function coerceDisplayValue(value: unknown, label: string): string | number {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    if (Number.isNaN(value) || !Number.isFinite(value)) return '—';
    return value;
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.value === 'number') return obj.value;
    if (typeof obj.amount === 'number') return obj.amount;
    // eslint-disable-next-line no-console
    console.warn(`[MetricCard] "${label}" received an object instead of a primitive:`, value);
    return '—';
  }
  return '—';
}

export function MetricCard({ label, value, subtitle, color = C.neon, icon, tooltip }: MetricCardProps) {
  const { fs } = useDensity();
  const safeValue = coerceDisplayValue(value, label);

  const inner = (
    <div style={{
      background: C.surface,
      border: `1px solid ${color}15`,
      borderRadius: 10,
      padding: '16px 18px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -20, left: -20,
        width: 80, height: 80,
        background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          fontSize: fs(FS.label), fontFamily: F.mono,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: C.muted, marginBottom: 8,
        }}>
          {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
          {label}
        </div>
        <div style={{
          fontSize: 28, fontFamily: F.mono, fontWeight: 700,
          color, textShadow: `0 0 16px ${color}40`, lineHeight: 1,
        }}>
          {safeValue}
        </div>
        {subtitle && (
          <div style={{ fontSize: fs(FS.small), fontFamily: F.display, color: C.dim, marginTop: 6 }}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );

  if (tooltip) return <EngineTooltip content={tooltip}>{inner}</EngineTooltip>;
  return inner;
}

// ─── LoadingShimmer ─────────────────────────────────────────────────────────

export function LoadingShimmer({ width = '100%', height = 20 }: { width?: string | number; height?: number }) {
  return (
    <div style={{
      width, height, borderRadius: 6,
      background: `linear-gradient(90deg, ${C.surface} 25%, ${C.panel} 50%, ${C.surface} 75%)`,
      backgroundSize: '200% 100%',
      animation: 'engine-shimmer 1.5s ease-in-out infinite',
    }} />
  );
}

// ─── EmptyState ─────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon?: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}

export function EmptyState({ icon = '📭', title, subtitle, action }: EmptyStateProps) {
  const { fs } = useDensity();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '60px 20px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.6 }}>{icon}</div>
      <div style={{ fontSize: fs(FS.heading + 2), fontWeight: 600, color: C.textSoft, marginBottom: 6 }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: fs(FS.body), color: C.muted, maxWidth: 360, marginBottom: 16 }}>
          {subtitle}
        </div>
      )}
      {action}
    </div>
  );
}
